import type { Integration, IntegrationCredentials } from "@repo/api";
import { Chip, Typography } from "@saviynt/design-system";
import { FormattedMessage } from "react-intl";
import {
  formatDate,
  getImportTimeDisplay,
  getMetadataValue,
  getStatusDisplay,
  SidePanelPlainRow,
} from "../../IntegrationSidePanel";

interface GoogleVertexIntegrationSidePanelContentProps {
  integration: Integration;
  credentials?: IntegrationCredentials;
  isLoadingCredentials: boolean;
  isCredentialsError: boolean;
}

export function GoogleVertexIntegrationSidePanelContent({
  integration,
}: GoogleVertexIntegrationSidePanelContentProps) {
  const metadata = integration.metadata || {};
  const statusDisplay = getStatusDisplay(integration.connectionStatus);
  const statusColor =
    statusDisplay === "Active"
      ? "success"
      : statusDisplay === "Pending"
        ? "warning"
        : "critical";
  const importTimeDisplay = getImportTimeDisplay(integration.features);

  return (
    <div className="flex flex-col gap-4 w-full Integrations-sidePanel-summaryContent">
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-1">
          <Typography kind="body3" color="--color-foreground-neutral-subtle">
            <FormattedMessage
              id="s2y5eG"
              defaultMessage="Status"
              description="Status label"
            />
          </Typography>
          <div>
            <Chip
              label={statusDisplay}
              color={statusColor}
              kind="subtleFilled"
              size="small"
            />
          </div>
        </div>
        <SidePanelPlainRow
          label={
            <FormattedMessage
              id="ExNLWm"
              defaultMessage="Application"
              description="Application label"
            />
          }
          value={integration.integrationType}
        />
        <SidePanelPlainRow
          label={
            <FormattedMessage
              id="QMyMOI"
              defaultMessage="Description"
              description="Description label"
            />
          }
          value={getMetadataValue(metadata, "description")}
        />
        <SidePanelPlainRow
          label={
            <FormattedMessage
              id="Vuk3cq"
              defaultMessage="Created on"
              description="Created on label"
            />
          }
          value={formatDate(integration.createdDate)}
        />
      </div>

      <div className="flex flex-col gap-4 p-4 border-t border-[var(--color-border-secondary-decorative,#f2f4f8)]">
        <Typography kind="body1-bold" color="--color-foreground-neutral-bold">
          <FormattedMessage
            id="j3KaLq"
            defaultMessage="Connection Details"
            description="Connection details label"
          />
        </Typography>
        <SidePanelPlainRow
          label={
            <FormattedMessage
              id="TNh5T5"
              defaultMessage="Project ID"
              description="Project ID field label"
            />
          }
          value={
            getMetadataValue(metadata, "projectId") !== "—"
              ? getMetadataValue(metadata, "projectId")
              : getMetadataValue(metadata, "project_id") !== "—"
                ? getMetadataValue(metadata, "project_id")
                : getMetadataValue(metadata, "accountId")
          }
        />
      </div>

      <div className="flex flex-col gap-4 p-4 border-t border-[var(--color-border-secondary-decorative,#f2f4f8)]">
        <Typography kind="body1-bold" color="--color-foreground-neutral-bold">
          <FormattedMessage
            id="Y8pgRN"
            defaultMessage="Scheduling"
            description="Scheduling section label"
          />
        </Typography>
        <SidePanelPlainRow
          label={
            <FormattedMessage
              id="+M6hy4"
              defaultMessage="Import Time (UTC)"
              description="Schedule import time label"
            />
          }
          value={importTimeDisplay}
        />
      </div>
    </div>
  );
}
